/**
 * EnvelopeCanvas.jsx — Generates a personalized envelope image using Canvas.
 *
 * The base envelope (blue envelope with gold padlock) is loaded from /envelope-base.png.
 * Fixed text: "The Valenciano Family" in upper-left (return address).
 * Personalized: Guest first name centered + "Your M-Vitation is Here" below.
 * Gold script styling with shine effect to match the return-address treatment.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const ENVELOPE_SRC = '/envelope-base.png';
const FONT_FAMILY = 'Great Vibes';
const FONT_URL = 'https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap';
const FONT_WOFF2 = 'https://fonts.gstatic.com/s/greatvibes/v19/RWmMoKWR9v4ksMfaWd_JN-XCg6UKDXlq.woff2';

// Gold gradient stops for text fill
const GOLD_TOP = '#E8D17C';
const GOLD_MID = '#C6A75A';
const GOLD_BOT = '#9B7420';

let fontLoaded = false;

async function ensureFont() {
  if (fontLoaded) return;
  try {
    const face = new FontFace(FONT_FAMILY, `url(${FONT_WOFF2})`);
    await face.load();
    document.fonts.add(face);
    fontLoaded = true;
  } catch {
    // Fallback — try loading via stylesheet
    if (!document.getElementById('great-vibes-font-link')) {
      const link = document.createElement('link');
      link.id = 'great-vibes-font-link';
      link.rel = 'stylesheet';
      link.href = FONT_URL;
      document.head.appendChild(link);
    }
    await document.fonts.load(`48px "${FONT_FAMILY}"`);
    fontLoaded = true;
  }
}

function drawGoldText(ctx, text, x, y, fontSize, align = 'center') {
  ctx.save();
  ctx.font = `${fontSize}px "${FONT_FAMILY}"`;
  ctx.textAlign = align;
  ctx.textBaseline = 'middle';

  // Create gold gradient
  const grad = ctx.createLinearGradient(x - 100, y - fontSize / 2, x + 100, y + fontSize / 2);
  grad.addColorStop(0, GOLD_TOP);
  grad.addColorStop(0.45, GOLD_MID);
  grad.addColorStop(0.55, '#E8D17C'); // shine highlight
  grad.addColorStop(1, GOLD_BOT);

  // Shadow for depth
  ctx.shadowColor = 'rgba(155,116,32,0.35)';
  ctx.shadowBlur = 3;
  ctx.shadowOffsetX = 1;
  ctx.shadowOffsetY = 1;

  ctx.fillStyle = grad;
  ctx.fillText(text, x, y);

  // Shine overlay pass
  ctx.shadowColor = 'transparent';
  ctx.globalAlpha = 0.15;
  ctx.fillStyle = '#FFFFF0';
  ctx.fillText(text, x, y - 1);
  ctx.globalAlpha = 1;

  ctx.restore();
}

export default function EnvelopeCanvas({ guestFirstName, onImageReady }) {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState(null);

  const render = useCallback(async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    // Load base image
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = ENVELOPE_SRC;
    });

    // Size canvas to image
    canvas.width = img.width;
    canvas.height = img.height;
    ctx.drawImage(img, 0, 0);

    // Load font
    await ensureFont();

    // The base image already contains:
    //   - "The Valenciano Family" upper-left (DO NOT TOUCH)
    //   - "Your M-Vitation is Here" centered lower area (DO NOT TOUCH)
    // Only add: guest first name ABOVE the existing centered line

    const centerX = img.width / 2;
    // Per user request (Feb 2026): name lowered from the previous 46% baseline.
    // Position kept on-image — dial in further via nameY coefficient if needed.
    const nameFontSize = Math.round(img.width * 0.085);
    const nameY = img.height * 0.52;

    drawGoldText(ctx, guestFirstName || 'Guest', centerX, nameY, nameFontSize, 'center');

    // Export
    const url = canvas.toDataURL('image/png');
    setDataUrl(url);
    if (onImageReady) onImageReady(url);
  }, [guestFirstName, onImageReady]);

  useEffect(() => { render(); }, [render]);

  return (
    <canvas
      ref={canvasRef}
      style={{ display: dataUrl ? 'none' : 'block', maxWidth: '100%' }}
    />
  );
}

export { EnvelopeCanvas };
